<template>
    <div ref="anchor"
        class="font-sans">
        <teleport v-if="props.visible"
            :to="teleportTarget">
            <!-- Focusable, so Escape after a click in the picker reaches it rather than the dialog. -->
            <div ref="picker"
                v-on-click-outside="() => emit('update:visible', false)"
                tabindex="-1"
                class="z-50 focus:outline-none"
                data-test="emoji-picker"
                @keydown.esc="closeOnEscape">
                <emoji-picker :data="emojiIndex"
                    :showPreview="false"
                    native
                    :perLine="8"
                    :emojiSize="20"
                    @select="select" />
            </div>

        </teleport>
    </div>
</template>

<script setup>
import { createPopper } from '@popperjs/core/lib/popper-lite';
import { usePanelTeleportTarget } from '@voxie/frontend-components';
import { Picker as EmojiPicker, EmojiIndex } from 'emoji-mart-vue-fast/src';
import data from "emoji-mart-vue-fast/data/all.json";
import { onBeforeUnmount, ref, watch } from 'vue';
import { vOnClickOutside } from '@vueuse/components'

const emojiIndex = ref(new EmojiIndex(data));

const props = defineProps({
    visible: {
        type: Boolean,
        required: true,
        default: false,
    },
    trigger: {
        type: HTMLElement,
    },
});

const emit = defineEmits(['selected', 'update:visible']);

const select = (event) => {
    emit('selected', event);
    emit('update:visible', false);
}

const closeOnEscape = (event) => {
    if (event.defaultPrevented) {
        return;
    }

    event.preventDefault();
    props.trigger?.focus();
    emit('update:visible', false);
}

const teleportTarget = usePanelTeleportTarget();
const anchor = ref();
const picker = ref();
let popper;

watch(picker, (element) => {
    popper?.destroy();
    popper = undefined;

    if (!element) {
        return;
    }

    popper = createPopper(anchor.value, element, {
        placement: 'top-start',
        // Fixed escapes the panel's overflow; on the page, absolute scrolls with it.
        strategy: teleportTarget.value !== 'body' ? 'fixed' : 'absolute',
    });

    // Focus once placed: focusing where the picker first renders scrolls the page.
    popper.forceUpdate();
    element.querySelector('input')?.focus();
}, { flush: 'post' });

onBeforeUnmount(() => popper?.destroy());

</script>

<style scoped>
@reference "@css/style.css";

:deep(.emoji-mart) {
    @apply border font-sans border-solid border-white bg-white shadow-xl rounded-lg !w-[320px] !max-w-full !h-[320px];
}

:deep(.emoji-mart-search) {
    @apply m-0 px-4 pt-4 pb-1;
}

:deep(.emoji-mart-scroll) {
    @apply px-4;
}

:deep(.emoji-mart-search input) {
    @apply font-sans h-12 rounded-xl border-slate-300 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-50;
}

:deep(.emoji-mart-category) {
    @apply grid grid-cols-8 pt-4;
}

:deep(.emoji-mart-category.emoji-mart-no-results) {
    @apply flex text-center w-full;
}

:deep(.emoji-mart-category-label) {
    @apply col-span-8 font-sans text-sm font-bold text-black mb-0 py-0;
}


:deep(.emoji-mart-category .emoji-mart-emoji span) {
    @apply cursor-pointer;
}

:deep(.emoji-mart-bar.emoji-mart-bar-anchors) {
    @apply order-3 bg-slate-50 rounded-b-lg border-0;
}

:deep(.emoji-mart-anchor) {
    @apply text-slate-800;
}

:deep(.emoji-mart-anchor.emoji-mart-anchor-selected) {
    @apply !text-sky-500;
}

:deep(.emoji-mart-anchor-bar) {
    @apply hidden;
}
</style>
